import React from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Row,
  Col,
  Space,
  Card,
  Switch,
  Typography,
  Tag,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import CycleLogAttachments from '../CycleLogAttachments';
import { ContactSearchSelect } from '../ContactSearchSelect';
import { ItemSearchSelect } from '../ItemSearchSelect';
import { ItemTaxCalculationResult } from './cycleLogFormHelpers';

const { Option } = Select;
const { Text } = Typography;

export interface CycleLogInputsSectionProps {
  form: any;
  logType: string;
  inputsData: any[];
  subtotalInputs: number;
  totalTaxInputs: number;
  totalInputs: number;
  storehouses: any[];
  storehouseId?: any;
  applyCatalogTaxes: boolean;
  allItems: any[];
  dynamicItems: any[];
  setDynamicItems: React.Dispatch<React.SetStateAction<any[]>>;
  calculateItemTax: (item: any) => ItemTaxCalculationResult;
  register: any;
  cycleUid: string;
  isMobile?: boolean;
}

export const CycleLogInputsSection: React.FC<CycleLogInputsSectionProps> = ({
  form,
  logType,
  inputsData,
  subtotalInputs,
  totalTaxInputs,
  totalInputs,
  storehouses,
  storehouseId,
  applyCatalogTaxes,
  allItems,
  dynamicItems,
  setDynamicItems,
  calculateItemTax,
  register,
  cycleUid,
  isMobile = false,
}) => {
  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          <span>
            {logType === 'SUPPLY_PURCHASE' && '🛒 Insumos Comprados'}
            {logType === 'INPUT_APPLICATION' && '🌾 Insumos Consumidos de Bodega'}
            {logType === 'LABOR_LOG' && '🧑‍🌾 Mano de Obra / Jornales'}
            {logType === 'SERVICE_EXPENSE' && '🚚 Servicios y Gastos Externos'}
          </span>
          {totalInputs > 0 &&
            (logType === 'SUPPLY_PURCHASE' || logType === 'SERVICE_EXPENSE' ? (
              <div
                style={{
                  textAlign: 'right',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  lineHeight: 1.2,
                }}
              >
                <span style={{ fontSize: '11px', color: '#595959' }}>
                  Subtotal:{' '}
                  <b>
                    ${' '}
                    {subtotalInputs.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </b>
                  {totalTaxInputs > 0 && (
                    <span style={{ marginLeft: 6, color: '#fa8c16' }}>
                      + IVA:{' '}
                      <b>
                        ${' '}
                        {totalTaxInputs.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </b>
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: '13px',
                    color:
                      logType === 'SUPPLY_PURCHASE' ? '#fa8c16' : '#13c2c2',
                    fontWeight: 'bold',
                  }}
                >
                  Total{' '}
                  {logType === 'SUPPLY_PURCHASE' ? 'Compra' : 'Servicio'}: ${' '}
                  {totalInputs.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            ) : (
              <span
                style={{
                  fontSize: '13px',
                  color: logType === 'LABOR_LOG' ? '#722ed1' : '#1890ff',
                  fontWeight: 'bold',
                }}
              >
                Total {logType === 'LABOR_LOG' ? 'Mano de Obra' : 'Insumos'}: ${' '}
                {totalInputs.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            ))}
        </div>
      }
      style={{ marginBottom: 16 }}
      size="small"
    >
      {/* Storehouse: ONLY for INPUT_APPLICATION and SUPPLY_PURCHASE */}
      {(logType === 'INPUT_APPLICATION' || logType === 'SUPPLY_PURCHASE') &&
        storehouses.length > 1 && (
          <Form.Item
            name="storehouse_id"
            label={
              logType === 'SUPPLY_PURCHASE'
                ? 'Almacén de Destino (Recepción)'
                : 'Almacén de Origen (Insumos)'
            }
            preserve={true}
            rules={[
              {
                required: inputsData.some(
                  (i: any) => i?.register_type === 'PRODUCT' && i?.element_id
                ),
                message:
                  logType === 'SUPPLY_PURCHASE'
                    ? 'Seleccione el almacén de destino'
                    : 'Seleccione el almacén de origen',
              },
            ]}
          >
            <Select placeholder="Seleccionar bodega">
              {storehouses.map((s: any) => (
                <Option key={s.id} value={s.id}>
                  {s.name || s.alias}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      {(logType === 'INPUT_APPLICATION' || logType === 'SUPPLY_PURCHASE') &&
        storehouses.length === 1 && (
          <Form.Item
            name="storehouse_id"
            initialValue={storehouses[0].id}
            preserve={true}
            hidden
          >
            <Input />
          </Form.Item>
        )}

      {/* For SUPPLY_PURCHASE, SERVICE_EXPENSE and LABOR_LOG: provider/worker at header level */}
      {(logType === 'SUPPLY_PURCHASE' ||
        logType === 'SERVICE_EXPENSE' ||
        logType === 'LABOR_LOG') && (
        <>
          <Form.Item
            name="contact_id"
            label={
              logType === 'SUPPLY_PURCHASE'
                ? 'Proveedor (Compra de Insumos)'
                : logType === 'LABOR_LOG'
                ? 'Trabajador / Jornalero'
                : 'Proveedor (Servicio / Flete / Gasto)'
            }
            rules={[
              {
                required: true,
                message:
                  logType === 'LABOR_LOG'
                    ? 'Seleccione el trabajador'
                    : 'Seleccione el proveedor',
              },
            ]}
          >
            <ContactSearchSelect
              placeholder={
                logType === 'SUPPLY_PURCHASE'
                  ? 'Buscar proveedor de insumos...'
                  : logType === 'LABOR_LOG'
                  ? 'Buscar trabajador o jornalero...'
                  : 'Buscar proveedor o transportador...'
              }
              role="PROVIDER"
              onSelectContact={(contact) => {
                form.setFieldValue('contact_name', contact?.name || '');
              }}
            />
          </Form.Item>
          <Form.Item name="contact_name" hidden>
            <Input type="hidden" />
          </Form.Item>
          {(logType === 'SUPPLY_PURCHASE' || logType === 'SERVICE_EXPENSE') && (
            <div
              style={{
                marginBottom: 14,
                padding: '8px 12px',
                background:
                  logType === 'SUPPLY_PURCHASE' ? '#fffaf6' : '#e6fffb',
                border: `1px solid ${
                  logType === 'SUPPLY_PURCHASE' ? '#ffd591' : '#87e8de'
                }`,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <Space align="center">
                <Form.Item
                  name="apply_catalog_taxes"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch
                    checkedChildren="Con Impuestos"
                    unCheckedChildren="Sin IVA"
                  />
                </Form.Item>
                <Text style={{ fontSize: 12, color: '#595959' }}>
                  {applyCatalogTaxes
                    ? 'Aplicando impuestos configurados en catálogo'
                    : 'Registro sin impuestos (ej. régimen simplificado o no responsable de IVA)'}
                </Text>
              </Space>
              {totalTaxInputs > 0 && (
                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                  IVA Total: ${' '}
                  {totalTaxInputs.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Tag>
              )}
            </div>
          )}
        </>
      )}

      <Form.List name="inputs" initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const currentInput = inputsData?.[name] || {};
              const selectedElementId = currentInput.element_id;
              const selectedItem = allItems.find(
                (i: any) => i.id === selectedElementId
              );
              const isService = selectedItem?.register_type === 'SERVICE';
              const isFixedAsset =
                selectedItem?.register_type === 'FIXED_ASSET';
              const itemCode = selectedItem?.custom_id || selectedItem?.sku;
              const categoryLabel =
                register?.categoryOptions?.find(
                  (c: any) => c.key === selectedItem?.category
                )?.string_value || selectedItem?.category;
              const umLabel =
                register?.umOptions?.find(
                  (u: any) => u.key === selectedItem?.measure_unit
                )?.string_value || selectedItem?.measure_unit;

              const storeStock =
                selectedItem && storehouseId
                  ? selectedItem.inventory?.find(
                      (inv: any) =>
                        String(inv.id) === String(storehouseId) ||
                        String(inv.uid) === String(storehouseId) ||
                        String(inv.storehouse_id) === String(storehouseId)
                    )?.quantity ?? 0
                  : selectedItem?.inventory?.reduce(
                      (sum: number, inv: any) => sum + (inv.quantity || 0),
                      0
                    ) ??
                    selectedItem?.quantity ??
                    0;

              const inputQty = Number(currentInput.quantity || 0);
              const hasStockWarning =
                logType === 'INPUT_APPLICATION' &&
                !isService &&
                selectedItem &&
                storehouseId &&
                inputQty > storeStock;

              const isHeaderProvider =
                logType === 'SUPPLY_PURCHASE' ||
                logType === 'SERVICE_EXPENSE' ||
                logType === 'LABOR_LOG';
              const itemAllowedTypes: ('PRODUCT' | 'SERVICE' | 'FIXED_ASSET')[] =
                logType === 'INPUT_APPLICATION' || logType === 'SUPPLY_PURCHASE'
                  ? ['PRODUCT']
                  : ['SERVICE'];

              const cardBg =
                logType === 'SUPPLY_PURCHASE'
                  ? '#fffaf6'
                  : logType === 'LABOR_LOG'
                  ? '#f9f0ff'
                  : logType === 'SERVICE_EXPENSE'
                  ? '#e6fffb'
                  : '#fcfcfc';
              const cardBorder =
                logType === 'SUPPLY_PURCHASE'
                  ? '#ffd591'
                  : logType === 'LABOR_LOG'
                  ? '#d3adf7'
                  : logType === 'SERVICE_EXPENSE'
                  ? '#87e8de'
                  : '#e8e8e8';
              const totalColor =
                logType === 'SUPPLY_PURCHASE'
                  ? '#fa8c16'
                  : logType === 'LABOR_LOG'
                  ? '#722ed1'
                  : logType === 'SERVICE_EXPENSE'
                  ? '#13c2c2'
                  : '#1890ff';

              return (
                <div
                  key={key}
                  style={{
                    marginBottom: 12,
                    background: cardBg,
                    padding: isMobile ? '8px 6px' : 10,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: 6,
                  }}
                >
                  <Row gutter={[8, 4]} align="bottom">
                    <Col xs={24} sm={12} md={isHeaderProvider ? 10 : 6}>
                      <Form.Item
                        {...restField}
                        name={[name, 'element_id']}
                        label={
                          logType === 'LABOR_LOG'
                            ? 'Labor / Actividad'
                            : logType === 'SERVICE_EXPENSE'
                            ? 'Servicio / Concepto'
                            : 'Insumo'
                        }
                        rules={[{ required: true, message: 'Requerido' }]}
                        style={{ marginBottom: 4 }}
                      >
                        <ItemSearchSelect
                          placeholder={
                            logType === 'SUPPLY_PURCHASE'
                              ? 'Buscar insumo a comprar...'
                              : logType === 'LABOR_LOG'
                              ? 'Buscar labor o jornal...'
                              : logType === 'SERVICE_EXPENSE'
                              ? 'Buscar servicio o flete...'
                              : 'Buscar insumo en almacén...'
                          }
                          initialItems={allItems}
                          selectedStorehouseId={
                            logType === 'INPUT_APPLICATION'
                              ? storehouseId
                              : undefined
                          }
                          allowedRegisterTypes={itemAllowedTypes}
                          onItemSelect={(item) => {
                            if (item) {
                              if (!dynamicItems.some((i) => i.id === item.id)) {
                                setDynamicItems((prev) => [...prev, item]);
                              }
                              const isServ = item.register_type === 'SERVICE';
                              const itemPrice = isServ
                                ? Number(item.sell_price) ||
                                  Number(item.purchase_price) ||
                                  0
                                : Number(item.purchase_price) ||
                                  Number(item.sell_price) ||
                                  0;

                              const cur =
                                form.getFieldValue(['inputs', name]) || {};
                              form.setFieldValue(['inputs', name], {
                                ...cur,
                                element_id: item.id,
                                element_uid: item.uid,
                                name: item.name,
                                item_name: item.name,
                                item_code:
                                  item.custom_id || item.sku || '',
                                item_description: item.description || '',
                                register_type:
                                  item.register_type ||
                                  (isServ ? 'SERVICE' : 'PRODUCT'),
                                unit_price: itemPrice,
                              });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>

                    {/* Contact per row: Operator for INPUT_APPLICATION */}
                    {logType === 'INPUT_APPLICATION' && (
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'contact_id']}
                          label="Aplicado por (Opcional)"
                          style={{ marginBottom: 4 }}
                        >
                          <ContactSearchSelect
                            placeholder="Buscar responsable..."
                            role="PROVIDER"
                          />
                        </Form.Item>
                      </Col>
                    )}

                    <Col
                      xs={12}
                      sm={isHeaderProvider ? 6 : 8}
                      md={isHeaderProvider ? 4 : 3}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, 'quantity']}
                        label={
                          logType === 'LABOR_LOG'
                            ? 'Jornales / Cant.'
                            : 'Cant.'
                        }
                        rules={[{ required: true }]}
                        style={{ marginBottom: 4 }}
                      >
                        <InputNumber
                          min={0.01}
                          style={{ width: '100%' }}
                          suffix={selectedItem?.measure_unit || undefined}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={12}
                      sm={isHeaderProvider ? 6 : 8}
                      md={isHeaderProvider ? 4 : 3}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, 'unit_price']}
                        label={
                          logType === 'SUPPLY_PURCHASE'
                            ? 'Precio Compra U.'
                            : logType === 'LABOR_LOG'
                            ? 'Valor Jornal'
                            : logType === 'SERVICE_EXPENSE'
                            ? 'Valor Unitario'
                            : 'Costo U.'
                        }
                        tooltip={
                          logType === 'INPUT_APPLICATION'
                            ? 'Costo unitario obtenido automáticamente del catálogo/inventario.'
                            : undefined
                        }
                        style={{ marginBottom: 4 }}
                      >
                        <InputNumber
                          min={0}
                          readOnly={logType === 'INPUT_APPLICATION'}
                          style={{
                            width: '100%',
                            ...(logType === 'INPUT_APPLICATION'
                              ? {
                                  backgroundColor: '#f5f5f5',
                                  color: '#595959',
                                  cursor: 'default',
                                }
                              : {}),
                          }}
                          placeholder="$"
                          formatter={(value) =>
                            `$ ${value}`.replaceAll(
                              /\B(?=(\d{3})+(?!\d))/g,
                              ','
                            )
                          }
                          parser={(value) =>
                            value
                              ? (value.replaceAll(/\$\s?|(,*)/g, '') as any)
                              : ''
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={12}
                      sm={isHeaderProvider ? 6 : 8}
                      md={isHeaderProvider ? 4 : 3}
                    >
                      <Form.Item label="Total" style={{ marginBottom: 4 }}>
                        <span
                          style={{
                            fontWeight: 'bold',
                            display: 'inline-block',
                            paddingTop: 5,
                            color: totalColor,
                            fontSize: 13,
                          }}
                        >
                          ${' '}
                          {(logType === 'SUPPLY_PURCHASE' ||
                          logType === 'SERVICE_EXPENSE'
                            ? (Number(currentInput?.quantity) || 0) *
                                (Number(currentInput?.unit_price) || 0) +
                              calculateItemTax(currentInput).taxAmount
                            : (Number(currentInput?.quantity) || 0) *
                              (Number(currentInput?.unit_price) || 0)
                          ).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </Form.Item>
                    </Col>
                    {/* Row attachments for INPUT_APPLICATION */}
                    {logType === 'INPUT_APPLICATION' && (
                      <Col xs={8} sm={20} md={3}>
                        <Form.Item
                          {...restField}
                          name={[name, 'attachments']}
                          label={isMobile ? 'Adj.' : 'Comprobante'}
                          valuePropName="value"
                          style={{ marginBottom: 4 }}
                        >
                          <CycleLogAttachments cycleUid={cycleUid} />
                        </Form.Item>
                      </Col>
                    )}
                    <Col
                      xs={4}
                      sm={4}
                      md={2}
                      style={{ textAlign: 'center', paddingBottom: 5 }}
                    >
                      <Form.Item
                        label=" "
                        style={{ marginBottom: 4, padding: 0 }}
                      >
                        <Button
                          danger
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Fila detallada de metadatos del elemento */}
                  {selectedItem && (
                    <div
                      style={{
                        marginTop: 6,
                        padding: '4px 8px',
                        background: hasStockWarning
                          ? '#fff2f0'
                          : logType === 'SUPPLY_PURCHASE'
                          ? '#fff'
                          : logType === 'SERVICE_EXPENSE'
                          ? '#f6ffed'
                          : '#fff',
                        borderRadius: 4,
                        border: `1px solid ${
                          hasStockWarning
                            ? '#ffccc7'
                            : logType === 'SUPPLY_PURCHASE'
                            ? '#ffe7ba'
                            : logType === 'SERVICE_EXPENSE'
                            ? '#b7eb8f'
                            : '#d9d9d9'
                        }`,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                      }}
                    >
                      <Tag
                        color={
                          isService
                            ? 'geekblue'
                            : isFixedAsset
                            ? 'purple'
                            : 'green'
                        }
                        style={{
                          margin: 0,
                          fontSize: 10,
                          lineHeight: '18px',
                          padding: '0 5px',
                        }}
                      >
                        {isService
                          ? 'Servicio/Labor'
                          : isFixedAsset
                          ? 'Activo Fijo'
                          : 'Insumo Físico'}
                      </Tag>
                      {itemCode && (
                        <Tag
                          bordered={false}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            background: '#fff',
                            color: '#595959',
                            padding: '0 4px',
                            border: '1px solid #d9d9d9',
                          }}
                        >
                          Ref: <strong>{itemCode}</strong>
                        </Tag>
                      )}
                      {categoryLabel && categoryLabel !== 'VRS' && (
                        <Tag
                          bordered={false}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            background: '#fff',
                            color: '#595959',
                            padding: '0 4px',
                            border: '1px solid #d9d9d9',
                          }}
                        >
                          Cat: {categoryLabel}
                        </Tag>
                      )}
                      {selectedItem.measure_unit && (
                        <Tag
                          bordered={false}
                          style={{
                            margin: 0,
                            fontSize: 10,
                            background: '#e6f7ff',
                            color: '#096dd9',
                            padding: '0 4px',
                          }}
                        >
                          UM:{' '}
                          <strong>
                            {umLabel || selectedItem.measure_unit}
                          </strong>
                        </Tag>
                      )}
                      {(logType === 'SUPPLY_PURCHASE' ||
                        logType === 'SERVICE_EXPENSE') &&
                        (() => {
                          const rowTax = calculateItemTax(currentInput);
                          return rowTax.taxAmount > 0 ? (
                            <Tag
                              color="orange"
                              style={{
                                margin: 0,
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                            >
                              {rowTax.taxDescription}: +$
                              {rowTax.taxAmount.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                              })}
                            </Tag>
                          ) : applyCatalogTaxes ? (
                            <Tag
                              style={{
                                margin: 0,
                                fontSize: 10,
                                color: '#8c8c8c',
                              }}
                            >
                              Sin IVA (0%)
                            </Tag>
                          ) : null;
                        })()}
                      {logType === 'INPUT_APPLICATION' && !isService && (
                        <span
                          style={{
                            color:
                              storeStock > 0
                                ? hasStockWarning
                                  ? '#cf1322'
                                  : '#389e0d'
                                : '#cf1322',
                            fontWeight: 600,
                            marginLeft: 2,
                          }}
                        >
                          Stock disponible en bodega: {storeStock}{' '}
                          {umLabel || selectedItem.measure_unit}
                          {hasStockWarning && ` (⚠️ Supera el disponible)`}
                        </span>
                      )}
                      {logType === 'SUPPLY_PURCHASE' && !isService && (
                        <span
                          style={{
                            color: '#389e0d',
                            fontWeight: 600,
                            marginLeft: 2,
                          }}
                        >
                          Stock en bodega: {storeStock}{' '}
                          {umLabel || selectedItem.measure_unit} (+
                          {inputQty || 0} tras compra)
                        </span>
                      )}
                      {selectedItem.purchase_price ? (
                        <span
                          style={{
                            color: '#8c8c8c',
                            marginLeft: isMobile ? 0 : 'auto',
                            width: isMobile ? '100%' : 'auto',
                          }}
                        >
                          Costo catálogo: $
                          {Number(
                            selectedItem.purchase_price
                          ).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                {logType === 'SUPPLY_PURCHASE' && 'Agregar Insumo Comprado'}
                {logType === 'INPUT_APPLICATION' && 'Agregar Insumo de Bodega'}
                {logType === 'LABOR_LOG' && 'Agregar Labor / Jornal'}
                {logType === 'SERVICE_EXPENSE' && 'Agregar Servicio / Gasto'}
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </Card>
  );
};
